import { cn } from '@/lib/cn';
import { Minus, Plus } from 'lucide-react';

/**
 * Stepper — `−` button | number display | `+` button. Used on Screen 3
 * for picking `perDay`. Bounds and step are passed in by the parent so
 * the same component handles all three goal types.
 *
 * The number display is intentionally large (Fraunces 40px) — matches
 * the rhythm of the other wizard headers and gives the pre-school
 * audience a visible target.
 *
 * Press feedback uses Tailwind's `active:scale-95` rather than the
 * spec's `0.92` because it stays inside the existing animation
 * vocabulary.
 */
export interface StepperProps {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  unitLabel?: string;
  ariaLabel?: string;
}

export function Stepper({
  value,
  min,
  max,
  step = 1,
  onChange,
  unitLabel,
  ariaLabel,
}: StepperProps) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  const decrement = () => onChange(clamp(value - step));
  const increment = () => onChange(clamp(value + step));

  return (
    <div
      className="flex flex-col items-center gap-2"
      role="group"
      aria-label={ariaLabel ?? 'Stepper'}
    >
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={decrement}
          disabled={value <= min}
          aria-label="Decrease"
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full bg-or-orange-500 text-white shadow-or-btn',
            'transition-transform active:scale-95 hover:brightness-110',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          <Minus className="h-5 w-5" aria-hidden />
        </button>

        <div
          className="min-w-[5rem] text-center font-display text-[40px] font-bold leading-none text-or-slate-700 tabular-nums"
          aria-live="polite"
        >
          {value}
        </div>

        <button
          type="button"
          onClick={increment}
          disabled={value >= max}
          aria-label="Increase"
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-full bg-or-orange-500 text-white shadow-or-btn',
            'transition-transform active:scale-95 hover:brightness-110',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          <Plus className="h-5 w-5" aria-hidden />
        </button>
      </div>
      {unitLabel ? (
        <span className="text-[12px] font-medium text-or-slate-500">{unitLabel}</span>
      ) : null}
    </div>
  );
}
